/**
 * Cable Junction Service
 *
 * CRUD operations for cable junction boxes on floor plans.
 * Junctions are bifurcation points where multiple cable paths can connect.
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type {
  CableJunction,
  CableJunctionRow,
  CreateCableJunction,
  UpdateCableJunction,
} from '@/types/cablePaths';

/**
 * Fetch all cable junctions for a floor plan.
 */
export async function getCableJunctions(floorPlanId: string): Promise<CableJunction[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('cable_junctions')
    .select('*')
    .eq('floor_plan_id', floorPlanId)
    .order('name');

  if (error) throw error;

  return (data ?? []).map((row: CableJunctionRow) => ({
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    x: row.x,
    y: row.y,
    product_id: row.product_id,
    notes: row.notes,
    // Scale factor
    scale: (row as any).scale ?? 1.0,
    // Purpose label fields
    purpose_type: (row as any).purpose_type ?? null,
    purpose_label: (row as any).purpose_label ?? null,
    label_offset_x: (row as any).label_offset_x ?? 25,
    label_offset_y: (row as any).label_offset_y ?? -15,
    label_width: (row as any).label_width ?? 120,
    label_font_size: (row as any).label_font_size ?? 10,
    // Timestamps
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  }));
}

/**
 * Get the next junction name (J1, J2, etc.) for a floor plan.
 */
export async function getNextJunctionName(floorPlanId: string): Promise<string> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase.rpc('get_next_junction_name', {
    fp_id: floorPlanId,
  });

  if (error) throw error;
  return data as string;
}

/**
 * Create a new cable junction.
 * If name is not provided, auto-generates the next available name (J1, J2, etc.).
 */
export async function createCableJunction(
  junction: Omit<CreateCableJunction, 'name'> & { name?: string },
): Promise<CableJunction> {
  const supabase = await getAuthenticatedSupabaseClient();

  const explicitName = !!junction.name;
  // VF-48: (floor_plan_id, name) is now UNIQUE. Two concurrent auto-name
  // creations can race on the same computed "next" name → 23505. For
  // auto-generated names, recompute and retry a few times. For an explicitly
  // chosen name that collides, surface the error (the user picked a duplicate).
  let data: unknown = null;
  let lastError: any = null;
  for (let attempt = 0; attempt < 4; attempt++) {
    let name = junction.name;
    if (!name) {
      name = await getNextJunctionName(junction.floor_plan_id);
    }

    const res = await supabase
      .from('cable_junctions')
      .insert({
        ...junction,
        name,
      })
      .select()
      .single();

    if (!res.error) {
      data = res.data;
      lastError = null;
      break;
    }
    lastError = res.error;
    // Only retry the auto-name race; explicit-name collisions are user error.
    if (res.error.code === '23505' && !explicitName) {
      continue;
    }
    throw res.error;
  }

  if (lastError) throw lastError;

  const row = data as CableJunctionRow;
  return {
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    x: row.x,
    y: row.y,
    product_id: row.product_id,
    notes: row.notes,
    // Scale factor
    scale: (row as any).scale ?? 1.0,
    // Purpose label fields
    purpose_type: (row as any).purpose_type ?? null,
    purpose_label: (row as any).purpose_label ?? null,
    label_offset_x: (row as any).label_offset_x ?? 25,
    label_offset_y: (row as any).label_offset_y ?? -15,
    label_width: (row as any).label_width ?? 120,
    label_font_size: (row as any).label_font_size ?? 10,
    // Timestamps
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  };
}

/**
 * Update a cable junction.
 */
export async function updateCableJunction(
  id: string,
  updates: UpdateCableJunction,
): Promise<CableJunction> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('cable_junctions')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;

  const row = data as CableJunctionRow;
  return {
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    x: row.x,
    y: row.y,
    product_id: row.product_id,
    notes: row.notes,
    // Scale factor
    scale: (row as any).scale ?? 1.0,
    // Purpose label fields
    purpose_type: (row as any).purpose_type ?? null,
    purpose_label: (row as any).purpose_label ?? null,
    label_offset_x: (row as any).label_offset_x ?? 25,
    label_offset_y: (row as any).label_offset_y ?? -15,
    label_width: (row as any).label_width ?? 120,
    label_font_size: (row as any).label_font_size ?? 10,
    // Timestamps
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  };
}

/**
 * Delete a cable junction.
 * Will fail if the junction has connected cable paths (enforced by RLS policy).
 */
export async function deleteCableJunction(id: string): Promise<boolean> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase.from('cable_junctions').delete().eq('id', id).select('id');

  if (error) {
    // Check if it's the "connected paths exist" error from RLS policy
    if (error.message.includes('violates row-level security') || error.code === '42501') {
      throw new Error(
        'Cannot delete junction: it has connected cable paths. Remove or reassign the paths first.',
      );
    }
    throw error;
  }

  if (!data || data.length === 0) {
    throw new Error(`Cable junction ${id} was not deleted`);
  }

  return true;
}

/**
 * Get IDs of cable paths connected to a junction.
 */
export async function getConnectedPathIds(junctionId: string): Promise<string[]> {
  const supabase = await getAuthenticatedSupabaseClient();
  const { data, error } = await supabase
    .from('cable_paths')
    .select('id')
    .or(`start_junction_id.eq.${junctionId},end_junction_id.eq.${junctionId}`);

  if (error) throw error;
  return (data ?? []).map(row => row.id);
}

/**
 * Check if a junction can be deleted (has no connected paths).
 */
export async function canDeleteJunction(junctionId: string): Promise<boolean> {
  const connectedPaths = await getConnectedPathIds(junctionId);
  return connectedPaths.length === 0;
}

/**
 * Move a junction to a new position.
 * Note: Connected paths will automatically get updated coordinates
 * since they derive start/end from junction positions at runtime.
 */
export async function moveCableJunction(id: string, x: number, y: number): Promise<CableJunction> {
  return updateCableJunction(id, { x, y });
}

// Export as a service object for consistency with other services
export const cableJunctionService = {
  getAll: getCableJunctions,
  getNextName: getNextJunctionName,
  create: createCableJunction,
  update: updateCableJunction,
  delete: deleteCableJunction,
  getConnectedPathIds,
  canDelete: canDeleteJunction,
  move: moveCableJunction,
};
